import React from 'react';
import { Path, Controller, useFormContext } from 'react-hook-form';
import { Box, Typography, Stack, Chip } from '@mui/material';
import { ReviewFormValues, OTHER_SENTINEL } from '../types';
import { capitalizeName } from '../../../../shared/utils';

interface TagSelectorProps {
    name: Path<ReviewFormValues>;
    options: readonly string[];
    label: string;
    helper?: string;
}

const TagSelector: React.FC<TagSelectorProps> = ({
    name,
    options,
    label,
    helper,
}) => {
    const { control, watch } = useFormContext<ReviewFormValues>();
    const selected: string[] = watch(name) ?? [];

    return (
        <Controller
            key={name as string}
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                const toggleTag = (tag: string) => {
                    const next = new Set<string>(
                        Array.isArray(field.value) ? field.value : [],
                    );
                    next.has(tag) ? next.delete(tag) : next.add(tag);
                    field.onChange(Array.from(next));
                };
                return (
                    <Box>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>
                            {label}
                        </Typography>
                        <Stack
                            direction='row'
                            spacing={1}
                            flexWrap='wrap'
                            useFlexGap
                        >
                            {[...options, OTHER_SENTINEL].map((tag) => {
                                const active = selected.includes(tag);
                                return (
                                    <Chip
                                        key={tag}
                                        label={
                                            tag === OTHER_SENTINEL
                                                ? 'Something else'
                                                : capitalizeName(tag)
                                        }
                                        clickable
                                        color={active ? 'primary' : 'default'}
                                        variant={active ? 'filled' : 'outlined'}
                                        onClick={() => toggleTag(tag)}
                                        sx={{ mb: 1 }}
                                        aria-pressed={active}
                                    />
                                );
                            })}
                        </Stack>
                        {helper && (
                            <Typography variant='caption' color='text.primary'>
                                {helper}
                            </Typography>
                        )}
                        {fieldState.error && (
                            <Typography
                                color='error'
                                variant='caption'
                                sx={{ display: 'block', mt: 0.5 }}
                            >
                                {fieldState.error.message as string}
                            </Typography>
                        )}
                    </Box>
                );
            }}
        />
    );
};

export default TagSelector;
